import { Helmet } from 'react-helmet-async';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Cookie Policy | WLE - Industrial Tank & Metal Fabrication</title>
        <meta name="description" content="Cookie Policy for WLE Technology Ventures Sdn. Bhd. and WLE Ventures. Learn how we use cookies on our website." />
      </Helmet>
      <Navigation />
      <main className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Cookie Policy</h1>
            <p className="text-muted-foreground">Last updated: December 13, 2025</p>
          </div>

          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                This Cookie Policy explains how WLE Technology Ventures Sdn. Bhd. and WLE Ventures (collectively referred to as "WLE", "we", "us", or "our") use cookies and similar technologies when you visit our website at wletec.com. By continuing to use our website, you consent to our use of cookies as described in this policy.
              </p>
            </section>

            {/* What Are Cookies */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">What Are Cookies?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookies are small text files placed on your device when you visit a website. They help websites remember your preferences, understand how you use the site, and improve your overall experience. Cookies can be "persistent" (remaining on your device until deleted) or "session-based" (deleted when you close your browser).
              </p>
            </section>

            {/* Types of Cookies We Use */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-wle-red">Essential Cookies</h3>
                  <p className="text-muted-foreground leading-relaxed mb-2">
                    These cookies are necessary for our website to function properly and cannot be disabled. They enable core functionality such as:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                    <li>Remembering your cookie consent preferences</li>
                    <li>Maintaining security and preventing fraud</li>
                    <li>Enabling basic site navigation and access to secure areas</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2 text-wle-red">Analytics Cookies</h3>
                  <p className="text-muted-foreground leading-relaxed mb-2">
                    These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They allow us to:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                    <li>Count visitors and see how they move around the site</li>
                    <li>Identify which pages are most and least popular</li>
                    <li>Improve website performance and user experience</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-2 text-sm italic">
                    Note: Analytics cookies are only loaded if you choose "Accept All" on our cookie consent banner.
                  </p>
                </div>
              </div>
            </section>

            {/* Managing Your Cookie Preferences */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">Managing Your Cookie Preferences</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When you first visit our website, you will see a cookie consent banner that allows you to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-4">
                <li><strong>Accept All:</strong> Enable both essential and analytics cookies</li>
                <li><strong>Essential Only:</strong> Enable only cookies necessary for the website to function</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                You can also control cookies through your browser settings. Most browsers allow you to refuse cookies or delete existing cookies. However, please note that disabling essential cookies may affect the functionality of our website.
              </p>
            </section>

            {/* Browser Cookie Settings */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">Browser Cookie Settings</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You can manage cookies in your browser settings. Here are links to cookie management instructions for common browsers:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-wle-blue hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-wle-blue hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-wle-blue hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-wle-blue hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            {/* Third-Party Cookies */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">Third-Party Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may use third-party services that set their own cookies on your device. These services include analytics providers that help us understand website usage. These third parties have their own privacy policies governing the use of cookies. We encourage you to review the privacy policies of any third-party services you interact with through our website.
              </p>
            </section>

            {/* Data Retention */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookie consent preferences are stored in your browser's local storage and persist until you clear your browser data or manually reset your preferences. Analytics data collected through cookies is retained in accordance with the data retention policies of our analytics providers.
              </p>
            </section>

            {/* Updates to This Policy */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">Updates to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy periodically. The "Last updated" date at the top of this page indicates when the policy was last revised.
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-wle-blue">Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have any questions about this Cookie Policy or our use of cookies, please contact us:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-background/50 p-4 rounded-lg border border-border">
                  <h3 className="font-semibold text-wle-red mb-2">WLE Technology Ventures Sdn. Bhd.</h3>
                  <p className="text-muted-foreground text-sm">
                    202601037405 (1699500-H)<br />
                    7, Jalan Utarid U5/17<br />
                    Mahsing Integrated Industrial Park<br />
                    Seksyen U5, 40150 Shah Alam<br />
                    Selangor Darul Ehsan, Malaysia<br />
                    Phone: +6011 3906 9019<br />
                    Email: enquiry@wletec.com
                  </p>
                </div>
                
                <div className="bg-background/50 p-4 rounded-lg border border-border">
                  <h3 className="font-semibold text-wle-red mb-2">WLE Ventures (Malaysia)</h3>
                  <p className="text-muted-foreground text-sm">
                    7, Jalan Utarid U5/17<br />
                    Mahsing Integrated Industrial Park<br />
                    Seksyen U5, 40150 Shah Alam<br />
                    Selangor Darul Ehsan, Malaysia<br />
                    Phone: +6011 3906 9019<br />
                    Email: enquiry@wletec.com
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CookiePolicy;
